import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  VStack,
  Text,
  Spinner,
  Center,
  Alert,
  AlertIcon,
  Image,
  Button,
} from '@chakra-ui/react'
import { FileItem } from '@/types'
import apiService from '@/services/api'
import { useAppTranslation } from '@/contexts/LanguageContext'

interface ImageViewerProps {
  file: FileItem
  scale: number
  onScaleChange: (scale: number) => void
}

export default function ImageViewer({ file, scale, onScaleChange }: ImageViewerProps) {
  const [rotation, setRotation] = useState(0)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [imageUrl, setImageUrl] = useState<string | null>(null)
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null)
  const { t } = useAppTranslation('files')

  const containerRef = useRef<HTMLDivElement>(null)
  const imageRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    setIsLoading(true)
    setError(null)
    const url = apiService.getFilePreviewUrl(file.id)
    setImageUrl(url)
    setIsLoading(false)
  }, [file])

  const handleImageLoad = () => {
    setIsLoading(false)
    setTimeout(() => {
      if (!containerRef.current || !imageRef.current) return
      const container = containerRef.current
      const img = imageRef.current
      const containerWidth = container.clientWidth - 32
      const containerHeight = container.clientHeight - 32
      const imageWidth = img.naturalWidth
      const imageHeight = img.naturalHeight
      if (imageWidth === 0 || imageHeight === 0) return
      setNaturalSize({ w: imageWidth, h: imageHeight })
      const fitScale = Math.min(containerWidth / imageWidth, containerHeight / imageHeight, 1.0)
      onScaleChange(fitScale)
    }, 100)
  }

  const handleImageError = () => {
    setError(t('imageViewer.loadFailed'))
    setIsLoading(false)
  }

  if (isLoading) {
    return (
      <Center h="full">
        <VStack spacing={4}>
          <Spinner size="xl" color="brand.500" />
          <Text color="gray.500">{t('imageViewer.loading')}</Text>
        </VStack>
      </Center>
    )
  }

  if (error) {
    return (
      <Center h="full">
        <Alert status="error" maxW="md" borderRadius="lg">
          <AlertIcon />
          <VStack align="start" spacing={2}>
            <Text fontWeight="semibold">{t('imageViewer.loadFailed')}</Text>
            <Text fontSize="sm">{error}</Text>
            <Button
              size="sm"
              onClick={() => {
                setIsLoading(true)
                setError(null)
                setImageUrl(apiService.getFilePreviewUrl(file.id))
                setIsLoading(false)
              }}
            >
              {t('imageViewer.retry')}
            </Button>
          </VStack>
        </Alert>
      </Center>
    )
  }

  return (
    <Box h="full" display="flex" flexDirection="column">
      <Box
        ref={containerRef}
        flex={1}
        overflow="auto"
        display="flex"
        bg="gray.50"
        p={4}
        className="ph-no-capture file-content-private"
        data-private="true"
      >
        {imageUrl && (
          <Image
            ref={imageRef}
            src={imageUrl}
            alt={file.original_name}
            width={naturalSize ? `${scale * naturalSize.w}px` : 'auto'}
            height="auto"
            maxW="none"
            maxH="none"
            flexShrink={0}
            m="auto"
            transform={`rotate(${rotation}deg)`}
            transformOrigin="center"
            transition="width 0.2s ease, transform 0.2s ease"
            onLoad={handleImageLoad}
            onError={handleImageError}
            boxShadow="lg"
            borderRadius="md"
            bg="white"
            p={2}
            className="ph-no-capture file-content-private"
            data-private="true"
          />
        )}
      </Box>
    </Box>
  )
}
